import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SectionTitle from '~/components/section-title/index';
import { useAtomValue } from 'jotai';
import { $CurrentArticleRelatedPost } from '@article/shared/atoms/current-article-related-post';
import { useViewTracking } from '~/utils/use-view-tracking';
import { SeeMore } from '~/components/see-more';
import { SeeMoreBanner } from '~/components/see-more-banner';
import { ArticleCard } from '~/components/article-card';
import { $ArticleRelatedPostLoadmore } from 'shared/atoms';
import { $CurrentArticle } from '@article/shared/atoms/current-article';
import { $CurrentPageModule } from '~/models/current-page-module';
import { ModuleType } from '~/utils/module-type';
import { ScopedProvider, useRouteLoader } from '@lemon8/web-app-shared/ssr';
import { DivisibleStack } from '~/components/divisible-stack';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import { ClickPosition, ClickPositionPrefix } from '~/utils/click-position';
import { Masonry, MasonryRef } from '~/components/masonry';
import ArticleRecommendCard from '~/components/article-recommend-card';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import cls from 'classnames';

import './style.scss';

export const ArticleRelatedPostSection: FC = () => {
  const { t } = useTranslation();

  const { groupId } = useAtomValue($CurrentArticle);

  const isMobileUser = useAtomValue($IsMobileUser);

  const relatedPosts = useAtomValue($CurrentArticleRelatedPost);

  const [bannerShow, setBannerShow] = useState<boolean>(true);

  const sectionObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_module_show', {
        current_page_module: ModuleType.related_article,
      });
    }, []),
  });

  const loader = useRouteLoader(
    'related-post-loadmore',
    {
      search_id: relatedPosts.searchId,
      offset: relatedPosts.cursor,
    },
    {
      atomFamilies: {
        $ArticleRelatedPostLoadmore,
      },
    },
  );

  const handleClickSeeMore = (): void => loader.load();

  const evenRelatedPosts = useMemo(() => {
    const filteredPosts = relatedPosts.items.filter((item) => item.groupId !== groupId);
    return filteredPosts.length % 2 === 0 ? filteredPosts : filteredPosts.slice(0, -1);
  }, [relatedPosts]);

  const masonryRef = useRef<MasonryRef>(null);

  useEffect(() => {
    if (loader.state === 'idle') {
      masonryRef.current?.postLayout(() => {
        // change banner show state after masonry completes layout
        setBannerShow(true);
      });
    } else {
      setBannerShow(false);
    }
  }, [loader.state]);

  if (relatedPosts.items.length === 0) {
    return null;
  }

  return (
    <section id="article-related-post" className={cls('article-recommend-post', { darkBackground: isMobileUser })}>
      <div ref={sectionObservedRef}>
        <SectionTitle title={t('wap_seo_recoomend_post')} />
      </div>
      <ScopedProvider
        atomsWithHydration={[
          [$CurrentPageModule, ModuleType.related_article],
          [$CurrentPositionPrefix, ClickPositionPrefix.none],
        ]}
      >
        {isMobileUser ? (
          <>
            <div className="recommend-masonry-container">
              <Masonry ref={masonryRef} columnCount={2} className="recommend-masonry">
                {evenRelatedPosts.map((item, index) => (
                  <ArticleRecommendCard
                    key={item.groupId}
                    pageModule={ModuleType.related_article}
                    clickPosition={ClickPosition.related_post}
                    article={item}
                    rank={index}
                  />
                ))}
              </Masonry>
            </div>
            {relatedPosts.hasMore && (
              <div
                className={cls('recommend-masonry-footer', {
                  'recommend-masonry-footer-loading': !bannerShow,
                })}
              >
                <SeeMoreBanner onClickSeeMore={handleClickSeeMore} loading={!bannerShow} />
              </div>
            )}
          </>
        ) : (
          <>
            <DivisibleStack divider="article-related-post-divider">
              {relatedPosts.items
                .filter((item) => item.groupId !== groupId)
                .map((item, rank) => (
                  <ArticleCard key={item.groupId} article={item} rank={rank} />
                ))}
            </DivisibleStack>
            {relatedPosts.hasMore && (
              <SeeMore onClickSeeMore={handleClickSeeMore} loading={loader.state === 'loading'} />
            )}
          </>
        )}
      </ScopedProvider>
    </section>
  );
};
